/**
 * PAGE: ADMIN — Numeros d'equipage reserves (VPI)
 * Permet a un admin/superadmin de bloquer manuellement un (product_id,
 * crew_number_int) afin que le prochain paiement BPI confirme ne l'attribue
 * pas automatiquement (TICKET-CRM-02, decision Floran 2026-04-23).
 */

import { useMemo, useState } from 'react';
import { Lock, Plus, Trash2 } from 'lucide-react';
import { PageHeader, PageLayout } from '../components/page';
import { ReservedCrewNumberModal } from '../components/modals';
import { ConfirmDialog, ErrorState, LoadingState, toast } from '../components/ui';
import { useReservedCrewNumbers } from '../hooks/useReservedCrewNumbers';
import { usePermissions } from '../hooks/usePermissions';
import type { ReservedCrewNumberInput } from '@/schemas/reservedCrewNumber';
import type { ReservedCrewNumberWithProduct } from '@/types/reservedCrewNumber';

export function ReservedCrewNumbersPage() {
  const { reservations, loading, error, create, remove } = useReservedCrewNumbers();
  const { role } = usePermissions();
  const canEdit = role === 'superadmin';

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    item: ReservedCrewNumberWithProduct | null;
  }>({ open: false, item: null });

  const grouped = useMemo(() => {
    const map = new Map<string, ReservedCrewNumberWithProduct[]>();
    for (const r of reservations) {
      const key = r.product?.id ?? r.product_id;
      const list = map.get(key) ?? [];
      list.push(r);
      map.set(key, list);
    }
    return Array.from(map.entries()).map(([productId, items]) => ({
      productId,
      productName: items[0]?.product?.name ?? 'Produit inconnu',
      items,
    }));
  }, [reservations]);

  const handleCreate = () => {
    setIsModalOpen(true);
  };

  const handleSubmit = async (data: ReservedCrewNumberInput) => {
    try {
      const result = await create(data);
      if (result) {
        toast.success('Numero reserve');
      } else {
        toast.error('Echec', {
          description:
            error?.message ?? 'Operation impossible (numero deja reserve ou droits insuffisants ?)',
        });
      }
    } catch (err) {
      console.error('Erreur ReservedCrewNumbersPage:', err);
      toast.error('Erreur', {
        description: err instanceof Error ? err.message : 'Operation impossible',
      });
    }
  };

  const handleDeleteClick = (item: ReservedCrewNumberWithProduct) => {
    setDeleteConfirm({ open: true, item });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.item) return;
    const success = await remove(deleteConfirm.item.id);
    if (success) {
      toast.success('Reservation supprimee');
    } else {
      toast.error('Echec de la suppression');
    }
    setDeleteConfirm({ open: false, item: null });
  };

  if (loading) {
    return (
      <PageLayout
        header={
          <PageHeader
            breadcrumbs={[{ label: 'Admin' }, { label: "Numeros d'equipage reserves" }]}
          />
        }
      >
        <LoadingState message="Chargement des reservations..." />
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout
        header={
          <PageHeader
            breadcrumbs={[{ label: 'Admin' }, { label: "Numeros d'equipage reserves" }]}
          />
        }
      >
        <ErrorState
          title="Erreur lors du chargement"
          message={error.message}
          onRetry={() => window.location.reload()}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      header={
        <PageHeader breadcrumbs={[{ label: 'Admin' }, { label: "Numeros d'equipage reserves" }]} />
      }
    >
      <div className="w-full h-px bg-zinc-200" />

      <div className="flex flex-col gap-6 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-semibold text-zinc-900">Numeros d'equipage reserves</h1>
            <p className="mt-1 text-sm text-zinc-600">
              Reserver un numero empeche son attribution automatique au prochain paiement BPI
              confirme. Le numero est libere des suppression de la reservation.
            </p>
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={handleCreate}
              className="inline-flex items-center gap-2 rounded-md bg-zinc-900 px-4 py-2 text-sm font-medium text-white hover:bg-zinc-800 transition-colors"
            >
              <Plus className="size-4" />
              Reserver un numero
            </button>
          )}
        </div>

        {reservations.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-zinc-300 bg-white py-16">
            <Lock className="size-10 text-zinc-300" />
            <p className="text-sm text-zinc-600">Aucun numero reserve.</p>
            {canEdit && (
              <button
                type="button"
                onClick={handleCreate}
                className="text-sm font-medium text-zinc-900 underline hover:no-underline"
              >
                Reserver le premier numero
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            {grouped.map((group) => (
              <div
                key={group.productId}
                className="overflow-hidden rounded-lg border border-zinc-200 bg-white"
              >
                <div className="border-b border-zinc-200 bg-zinc-50 px-4 py-2">
                  <h2 className="text-sm font-semibold text-zinc-900">{group.productName}</h2>
                </div>
                <table className="w-full">
                  <thead className="border-b border-zinc-200 bg-zinc-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-600">
                        Numero
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-600">
                        Raison
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-600">
                        Cree le
                      </th>
                      {canEdit && (
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-zinc-600">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {group.items.map((item) => (
                      <tr
                        key={item.id}
                        className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm font-mono font-medium text-zinc-900">
                          {item.crew_number_int}
                        </td>
                        <td className="px-4 py-3 text-sm text-zinc-700">
                          <div className="max-w-xl truncate">{item.reason}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-zinc-500">
                          {new Date(item.created_at).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                          })}
                        </td>
                        {canEdit && (
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => handleDeleteClick(item)}
                                className="inline-flex items-center justify-center size-8 rounded-md text-zinc-600 hover:bg-red-50 hover:text-red-600 transition-colors"
                                aria-label="Supprimer"
                                title="Supprimer"
                              >
                                <Trash2 className="size-4" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>

      {isModalOpen && (
        <ReservedCrewNumberModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleSubmit}
        />
      )}

      <ConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => setDeleteConfirm({ open, item: open ? deleteConfirm.item : null })}
        title="Supprimer la reservation"
        description={
          deleteConfirm.item
            ? `Liberer le numero ${deleteConfirm.item.crew_number_int} sur "${deleteConfirm.item.product?.name ?? 'ce produit'}" ? Le numero pourra etre attribue au prochain paiement BPI confirme.`
            : ''
        }
        confirmLabel="Supprimer"
        variant="destructive"
        onConfirm={handleDeleteConfirm}
      />
    </PageLayout>
  );
}
